import { useEffect } from 'react';
import { useGameStore, type AchievementToastItem } from '@/ui/store/gameStore';
import { Icon } from './Icon';

/**
 * AchievementToast — wyskakujące powiadomienia o odblokowanych osiągnięciach
 * (Etap 5.D.2d). Renderowany zawsze (mount w App), pokazuje stos toastów z
 * kolejki `achievementToasts`. Każdy toast auto-znika po ~4 s.
 *
 * Źródła unlocków (push do store):
 *  - stall buy (kupujący) / list-pet (wystawiający) — z odpowiedzi `stallApi`,
 *  - direct trade (obie strony online) — z `trade_completed` (realtime przekazał
 *    per-stronę), przez PlazaScene.
 *
 * Gra dla dziecka: duża ikona trofeum/medalu + nazwa + nagroda 💎. Stos
 * top-center, pod ewentualnym `TradeNotice`.
 */
export function AchievementToast(): JSX.Element | null {
  const toasts = useGameStore((s) => s.achievementToasts);
  if (toasts.length === 0) return null;

  return (
    <div className="absolute top-28 left-1/2 -translate-x-1/2 z-50 flex flex-col items-center gap-2 pointer-events-none">
      {toasts.map((t) => (
        <ToastCard key={t.key} item={t} />
      ))}
    </div>
  );
}

/** Pojedynczy toast — własny timer auto-dismiss (4 s) + slide-in. */
function ToastCard({ item }: { item: AchievementToastItem }): JSX.Element {
  const dismiss = useGameStore((s) => s.dismissAchievementToast);

  useEffect(() => {
    const t = setTimeout(() => dismiss(item.key), 4000);
    return () => clearTimeout(t);
  }, [item.key, dismiss]);

  return (
    <div
      className="bg-white rounded-2xl shadow-2xl border-2 border-meadow-300 px-4 py-2.5 flex items-center gap-3 min-w-[260px] max-w-[90vw]"
      style={{ animation: 'achievementToastIn 220ms ease-out backwards' }}
    >
      <Icon
        src="/ui/ui_btn_achievement.webp"
        fallback="🏅"
        alt="osiągnięcie"
        className="w-10 h-10 shrink-0"
      />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-bold text-meadow-500 uppercase tracking-wide">
          Osiągnięcie odblokowane!
        </div>
        <div className="font-bold text-meadow-700 truncate">{item.label}</div>
      </div>
      {item.crystalsReward > 0 && (
        <div className="inline-flex items-center gap-1 font-bold text-purple-700 shrink-0">
          +{item.crystalsReward}
          <Icon src="/ui/ui_gem.webp" fallback="💎" alt="gemy" className="w-4 h-4" />
        </div>
      )}
      <style>{`
        @keyframes achievementToastIn {
          from { opacity: 0; transform: translateY(-10px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>
    </div>
  );
}
